/**
 * A half-finished pass through the generate wizard, kept across a browser close.
 *
 * Setting up a roster is fifteen minutes of typing spread over four steps: who
 * reported, their ad-hoc counters, their RA/RO times, the SOS table, the counter
 * grid. All of that lived in React state, so a closed tab lost the lot and the
 * user came back to a wizard offering them the *previous* shift's inputs.
 *
 * So it is written to localStorage as it goes. sessionStorage is where the
 * session itself lives and it is emptied when the tab closes, which is exactly
 * the case this has to survive.
 *
 * Keyed by session, because a draft belongs to the roster it is being built for.
 * Two people on two sessions in one browser each keep their own.
 */

import type { ChopsSource } from './chopsInput';

const STORAGE_PREFIX = 'ac_roster_wizard_draft:';
const OPEN_PREFIX = 'ac_roster_wizard_open:';

/** Long enough to survive a shift handover, short enough not to haunt anyone. */
const MAX_AGE_HOURS = 18;

/**
 * Roughly what localStorage will take, leaving room for everything else in it.
 * A roster PDF is about 1 MB, so ~1.4 MB once base64-encoded — it fits, and
 * keeping it is what lets the user still switch operation mode after resuming.
 */
const MAX_DRAFT_BYTES = 3_500_000;

export interface WizardDraft {
  /** Which step the user had reached: 2, 3 or 4. */
  step: number;
  operationMode: 'arrival' | 'departure';
  shiftType: 'morning' | 'night';
  /** The officer table, exactly as typed. */
  officers: unknown[];
  /** The night default-SOS table, exactly as edited. */
  sosOfficers: unknown[];
  /** Superseded by the handwritten rows in `sosOfficers`; still read from drafts saved before that. */
  sosCandidates?: unknown[];
  supportOfficers: unknown[];
  /** Counters already taken at shift start — only meaningful at step 4. */
  occupiedCounters: Record<string, unknown>;
  /** Step 4's own grid: every counter the user has clicked or typed onto, and
   *  which way a clash with an occupied counter was decided. Lives inside
   *  CounterGrid itself, so without this a step-4 refresh would lose it even
   *  though steps 2-3 come back. */
  counterGridState?: Record<string, unknown>;
  counterGridChoices?: Record<string, unknown>;
  /** ACar/DCar officers chops left without a first counter, and step 4's decisions for them and
   *  for chops' unplaced SOS officers. */
  firstStints?: unknown[];
  unplacedDecisions?: Record<string, string>;
  /** Officers the finalize could seat only in part, awaiting keep/remove — the roster already holds
   *  them, so a resumed pass must come back to this rather than finalize again. */
  partialReview?: unknown;
  /** The unpublished session a regeneration is building into, if there is one. */
  draftSessionId: string | null;
  /** Officer counts right after generating, so step 4 keeps numbering OT keys
   *  from where the draft session actually got to rather than from this one. */
  postGenerateOfficerCounts?: { sos: number; ot: number } | null;
  /** The uploaded PDF, base64-encoded. Dropped when the draft would be too big. */
  pdfName?: string;
  pdfBase64?: string;
  detectedShift?: 'morning' | 'night' | null;
  detectedMode?: 'arrival' | 'departure' | null;
  /** The chops session the pass started from, when it did not start from an upload. */
  chops?: ChopsSource | null;
  /** Both modes' bundles from that session, so resuming can switch mode without asking chops again. */
  modeBundles?: Record<string, unknown>;
  /** The roster as the PDF prints it, and when its first slot starts — what the roster is generated from. */
  rosterTemplate?: Record<string, number[]> | null;
  /** The duties the PDF prints beside rosterTemplate (RosterDuties). */
  rosterDuties?: Record<string, Record<string, string>> | null;
  rosterStart?: string;
  savedAt: string;
}

function keyFor(sessionId: string): string {
  return `${STORAGE_PREFIX}${sessionId}`;
}

function isFresh(draft: WizardDraft): boolean {
  const age = Date.now() - new Date(draft.savedAt).getTime();
  return Number.isFinite(age) && age >= 0 && age < MAX_AGE_HOURS * 3600_000;
}

/**
 * Store the draft, dropping the PDF first if the whole thing will not fit.
 *
 * Losing the PDF costs the user the ability to switch operation mode without
 * uploading again; losing the draft costs them everything they typed. So when
 * only one can be kept, it is not the PDF.
 */
export function saveWizardDraft(sessionId: string, draft: WizardDraft): void {
  if (!sessionId) return;
  const write = (value: WizardDraft) =>
    localStorage.setItem(keyFor(sessionId), JSON.stringify(value));

  try {
    const full = JSON.stringify(draft);
    if (full.length <= MAX_DRAFT_BYTES) {
      try {
        localStorage.setItem(keyFor(sessionId), full);
        return;
      } catch {
        // Full: other sessions' drafts go before this one's PDF does.
        while (freeDraftSpace(sessionId)) {
          try {
            localStorage.setItem(keyFor(sessionId), full);
            return;
          } catch { /* still full */ }
        }
      }
    }
    write({ ...draft, pdfBase64: undefined });
  } catch {
    try {
      write({ ...draft, pdfBase64: undefined });
    } catch {
      // Storage is full or blocked. The wizard still works; it just will not
      // survive a close, which is no worse than before this existed.
    }
  }
}

/** When a stored draft was saved (ms), read without parsing the whole of it — a draft
 *  can be megabytes. NaN when it cannot be read. */
function savedAtOf(raw: string): number {
  const at = raw.lastIndexOf('"savedAt":"');
  if (at < 0) return NaN;
  const start = at + '"savedAt":"'.length;
  return new Date(raw.slice(start, raw.indexOf('"', start))).getTime();
}

/** Every stored draft: its session and when it was saved (oldest first). */
function storedDrafts(): Array<{ sessionId: string; savedAt: number }> {
  const drafts: Array<{ sessionId: string; savedAt: number }> = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key?.startsWith(STORAGE_PREFIX)) continue;
    drafts.push({ sessionId: key.slice(STORAGE_PREFIX.length), savedAt: savedAtOf(localStorage.getItem(key) ?? '') });
  }
  // Unreadable dates sort first: they are the first to go.
  const at = (d: { savedAt: number }) => (Number.isNaN(d.savedAt) ? -1 : d.savedAt);
  return drafts.sort((a, b) => at(a) - at(b));
}

/**
 * Drop drafts no one can resume any more — past MAX_AGE_HOURS, or unreadable — with
 * their open flags. Run once at start-up: a draft was otherwise only cleared when its own
 * session was opened again, so drafts for rosters never revisited stayed for good and
 * filled the storage.
 */
export function sweepStaleWizardDrafts(): void {
  try {
    const now = Date.now();
    for (const { sessionId, savedAt } of storedDrafts()) {
      const age = now - savedAt;
      if (!(age >= 0 && age < MAX_AGE_HOURS * 3600_000)) clearWizardDraft(sessionId);
    }
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(OPEN_PREFIX) && localStorage.getItem(keyFor(key.slice(OPEN_PREFIX.length))) === null) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Storage blocked: nothing stored to sweep.
  }
}

/**
 * Make room in a full localStorage by dropping one draft, the oldest — never `keep`'s,
 * nor, without one, the newest (most likely the wizard someone is in right now).
 * Returns false when there is none left to drop.
 */
export function freeDraftSpace(keep?: string): boolean {
  const drafts = storedDrafts();
  const spared = keep ?? drafts[drafts.length - 1]?.sessionId;
  const victim = drafts.find((d) => d.sessionId !== spared);
  if (!victim) return false;
  clearWizardDraft(victim.sessionId);
  return true;
}

export function loadWizardDraft(sessionId: string): WizardDraft | null {
  if (!sessionId) return null;
  try {
    const raw = localStorage.getItem(keyFor(sessionId));
    if (!raw) return null;
    const draft = JSON.parse(raw) as WizardDraft;
    if (!draft || typeof draft.step !== 'number' || !Array.isArray(draft.officers)) {
      return null;
    }
    if (!isFresh(draft)) {
      clearWizardDraft(sessionId);
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

/**
 * Whether the wizard is currently open, remembered across a page refresh.
 *
 * Kept apart from the draft because the two answer different questions: the
 * draft is what was typed, this is whether the user is in the middle of typing
 * it. Once someone has chosen to resume, refreshing should put them back in the
 * wizard rather than back at the offer to resume.
 */
export function setWizardOpen(sessionId: string, open: boolean): void {
  if (!sessionId) return;
  try {
    if (open) localStorage.setItem(`${OPEN_PREFIX}${sessionId}`, '1');
    else localStorage.removeItem(`${OPEN_PREFIX}${sessionId}`);
  } catch {
    // Only costs the user a click on Resume after a refresh.
  }
}

export function wasWizardOpen(sessionId: string): boolean {
  if (!sessionId) return false;
  try {
    return localStorage.getItem(`${OPEN_PREFIX}${sessionId}`) === '1';
  } catch {
    return false;
  }
}

export function clearWizardDraft(sessionId: string): void {
  if (!sessionId) return;
  try {
    localStorage.removeItem(keyFor(sessionId));
    localStorage.removeItem(`${OPEN_PREFIX}${sessionId}`);
  } catch {
    // Nothing to do — a draft that cannot be removed is still ignored once stale.
  }
}

/** Rebuild the uploaded PDF from a draft, so the wizard can re-parse it. */
export function draftPdfFile(draft: WizardDraft): File | null {
  if (!draft.pdfBase64 || !draft.pdfName) return null;
  try {
    const binary = atob(draft.pdfBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], draft.pdfName, { type: 'application/pdf' });
  } catch {
    return null;
  }
}

/** Base64-encode an uploaded PDF for storage, in chunks (see api/client.ts). */
export async function encodePdfForDraft(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const chunks: string[] = [];
  for (let i = 0; i < bytes.length; i += 0x8000) {
    chunks.push(String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000))));
  }
  return btoa(chunks.join(''));
}
